import React, { useState } from "react";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { WeeklyJobStatisticsResult } from "./jobStatisticsResult";
import OperationsService from "./operationsService";

interface WeeklyComparisonProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();

const uhcColumns: ColumnConfig = {
  avgSeconds: {
    displayText: "Average Seconds",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__average-seconds-output-uhc",
  },
  jobCount: {
    displayText: "Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-count-output-uhc",
  },
  yrWkNum: {
    displayText: "Year Week Number",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__yrWkNum-output-uhc",
  },
};

const getMostRecentWednesday = (): string => (d => (d.setDate(d.getDate() - (d.getDay() + 4) % 7), d.toISOString().split('T')[0]))(new Date());

const WeeklyComparison: React.FC<WeeklyComparisonProps> = ({ clients, clientId, setClientId }) => {
  const [jobStatistics, setJobStatistics] = useState<WeeklyJobStatisticsResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [lineOfBusiness, setLineOfBusiness] = useState<string>('');
  const [linesOfBusinessOptions, setLinesOfBusinessOptions] = useState<{ label: string; value: string }[]>([]);
  const [runDate, setRunDate] = useState<string>(getMostRecentWednesday());

  const calculateJobStatistics = async () => {
    if (!clientId) {
      setError("Please select a Client.");
      return;
    }

    if (!lineOfBusiness) {
      setError("Please select a Line of Business.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await operationsService.getWeeklyJobStatistics(
        clientId,
        lineOfBusiness,
        runDate
      );
      setJobStatistics(response);
    } catch (error) {
      setError("Error calculating job statistics");
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedClientId = Number(event.target.value);
    setClientId(selectedClientId);
    setLineOfBusiness('');
    setLinesOfBusinessOptions([]);
    setJobStatistics([]);

    if (!selectedClientId) return;

    try {
      const lobs = await operationsService.getLinesOfBusiness(selectedClientId);

      if (!lobs.length) {
        setError("No Line of Business options available for this client.");
        return;
      }

      const lobOptions = lobs.map(lob => ({
        label: lob.toUpperCase(),
        value: lob.toLowerCase()
      }));

      setLinesOfBusinessOptions(lobOptions);
      setError("");
    } catch {
      setError("Error fetching Line of Business options");
    }
  };

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col">
          <QESSelect
            id="clientSelect"
            value={clientId !== null ? clientId.toString() : ''}
            onChange={handleClientChange}
            placeholder="Select Client"
            testId="weekend-run-monitoring__client-select-uhc"
            label="Client"
            required
            isValid
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            id="lineOfBusinessSelect"
            value={lineOfBusiness}
            onChange={(event) => setLineOfBusiness(event.target.value)}
            placeholder="Select Line Of Business"
            testId="weekend-run-monitoring__line-of-business-select-uhc"
            label="Line of Business"
            required
            isValid
          >
            <option value="">Select Line Of Business</option>
            {linesOfBusinessOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="weekend-run-monitoring__run-date-input-uhc"
          />
        </div>
        <div className="col d-flex align-items-end">
          <QESButton onClick={calculateJobStatistics} qesType="primary" qesTestId="weekly-run-monitoring__calculate-weekly-job-statistics-uhc">
            Calculate Job Statistics
          </QESButton>
        </div>
      </div>
      <div className="mt-4">
        {loading && <div>Loading...</div>}
        {error && <div>{error}</div>}
        {jobStatistics.length > 0 && (
          <Waffles
            columns={uhcColumns}
            data={jobStatistics.map((stat, index) => ({
              /* eslint-disable sort-keys */
              key: index.toString(),
              yrWkNum: stat.yrWkNum,
              jobCount: stat.jobCount,
              avgSeconds: stat.avgSeconds,
              /* eslint-enable sort-keys */
            }))}
            enablePagination={false}
            passThroughSorting={true}
            testId="weekend-run-monitoring__job-statistics-table-uhc"
          />
        )}
      </div>
    </div>
  );
};

export default WeeklyComparison;